import { inject, Injectable } from '@angular/core';
import { Firestore, doc } from '@angular/fire/firestore';
import { getDoc } from 'firebase/firestore';
import { CollectionsService } from 'src/app/core/services/collections/collections.service';
import { ActiveInspectionContextService } from './active-inspection-context.service';
import {
  availabilityFromFirestoreError,
  documentPathForRecent,
  isRecentDocumentAvailable,
  nestedRecentAvailability,
  RecentAvailability,
} from './universal-search-availability';
import { UniversalSearchResult } from './universal-search.models';

export interface FilteredRecents {
  recents: UniversalSearchResult[];
  pruned: boolean;
}

/**
 * Recents live in localStorage, which cannot know when another user deletes a record. Before they
 * are shown, each entry is checked against the open inspection (zero reads) or a single getDoc.
 */
@Injectable({
  providedIn: 'root',
})
export class UniversalSearchAvailabilityService {
  private firestore = inject(Firestore);
  private collectionsService = inject(CollectionsService);
  private activeInspectionContextService = inject(ActiveInspectionContextService);

  public async filterExisting(workspaceId: string, recents: UniversalSearchResult[]): Promise<FilteredRecents> {
    const snapshot = this.activeInspectionContextService.currentSnapshot();
    const outcomes = await Promise.all(recents.map((recent) => this.availabilityOf(workspaceId, recent, snapshot)));

    const kept = recents.filter((_, index) => outcomes[index] !== 'gone');
    return {
      recents: kept,
      pruned: kept.length !== recents.length,
    };
  }

  private async availabilityOf(workspaceId: string, recent: UniversalSearchResult, snapshot: ReturnType<ActiveInspectionContextService['currentSnapshot']>): Promise<RecentAvailability> {
    const nested = nestedRecentAvailability(snapshot, workspaceId, recent);
    if (nested === true) return 'available';
    if (nested === false) return 'gone';

    const path = documentPathForRecent(this.collectionsService, workspaceId, recent);
    if (!path) return 'gone';

    try {
      const snapshotDoc = await getDoc(doc(this.firestore, path));
      if (!snapshotDoc.exists()) return 'gone';
      return isRecentDocumentAvailable(recent.kind, snapshotDoc.data()) ? 'available' : 'gone';
    } catch (error) {
      return availabilityFromFirestoreError(error);
    }
  }
}
